import * as React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ApiError, startGoogleAuth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Mark } from '@/components/layout/mark';
import { HiddenGlyph } from '@/components/visibility-band';
import { useSession } from './session';
import { cn } from '@/lib/utils';

/**
 * Sign in.
 *
 * Google only, and the role travels in a server-side state row rather than in
 * the URL — so this page cannot be edited into a different dashboard, and an
 * account registered as a client is refused here rather than switched.
 *
 * The failure reasons come back on the redirect and each gets its own sentence.
 * "Login failed" on a role mismatch produces a support ticket nobody can answer,
 * because the person on the other end has no way to know the account exists
 * under a different role.
 */

const AUTH_FAILURE_COPY: Record<string, { title: string; body: string }> = {
  state_expired: {
    title: 'The sign-in link timed out',
    body: 'Each one is single-use and short-lived. Start again.',
  },
  state_reused: {
    title: 'That sign-in link was already used',
    body: 'The state row is consumed on first use. Start again.',
  },
  role_mismatch: {
    title: 'That account is not an expert',
    body: 'The Google account you chose is registered here under a different role, and roles are immutable. Sign in with the account your operator set up as an expert.',
  },
  not_provisioned: {
    title: 'No expert account exists for that address',
    body: 'An operator creates the expert record and assigns the projects before the first sign-in. Ask them to add you.',
  },
  held: {
    title: 'Your account is held',
    body: 'A hold blocks claiming, prompting and promoting. Nothing has been deleted. An operator releases it.',
  },
  exchange_failed: {
    title: 'Google did not confirm that sign-in',
    body: 'Try again. If it keeps happening, try a different browser.',
  },
  denied: {
    title: 'Sign-in was cancelled',
    body: 'Access to your Google account is required to sign in.',
  },
};

const UNKNOWN_FAILURE = {
  title: 'Sign-in did not complete',
  body: 'Try again. If it keeps happening, tell an operator which address you used.',
};

export function LoginPage() {
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { status } = useSession();

  const failed = params.get('auth') === 'failed';
  const failure = failed ? (AUTH_FAILURE_COPY[params.get('reason') ?? ''] ?? UNKNOWN_FAILURE) : null;

  // Clear the failure out of the URL once it has been read, so a refresh does
  // not re-show a message about a sign-in attempt that is over.
  React.useEffect(() => {
    if (!failed) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      next.delete('auth');
      next.delete('reason');
      setParams(next, { replace: true });
    }, 15_000);
    return () => clearTimeout(timer);
  }, [failed, params, setParams]);

  if (status === 'authenticated') return <Navigate to="/queue" replace />;

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const { authorization_url } = await startGoogleAuth(`${window.location.origin}/auth/callback`);
      window.location.assign(authorization_url);
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError ? err.message : 'Sign-in could not be started. Try again in a moment.');
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_minmax(22rem,30rem)]">
      <ThesisPanel />

      <main className="flex items-center justify-center bg-ground px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden">
            <Mark className="h-6 w-6 text-ink" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold tracking-[-0.02em] text-ink lg:mt-0">Review console</h1>
          <p className="mt-1.5 text-sm leading-[19px] text-ink-2">
            Sign in with the Google account an operator registered as an expert.
          </p>

          {failure ? (
            <div className="mt-5 border border-danger bg-danger-soft px-3 py-2.5" role="alert">
              <p className="text-sm font-medium text-ink">{failure.title}</p>
              <p className="mt-1 text-xs leading-[17px] text-ink-2">{failure.body}</p>
            </div>
          ) : null}

          <Button variant="primary" size="lg" className="mt-6 w-full" loading={busy} onClick={() => void signIn()}>
            <GoogleGlyph />
            Continue with Google
          </Button>

          {error ? (
            <p className="mt-2 font-mono text-tiny text-danger" role="alert">
              {error}
            </p>
          ) : null}

          <p className="mt-6 border-t border-rule pt-4 text-xs leading-[17px] text-ink-3">
            Your role is captured server-side when this page starts the flow and read back from that row at the
            callback. It never travels in a query parameter, so this page cannot be edited into another dashboard.
          </p>
        </div>
      </main>
    </div>
  );
}

/**
 * The thesis panel.
 *
 * It draws the one thing that makes this dashboard different from the client's:
 * a single thread with two tracks. Turns the client reads sit clean on the rail;
 * turns only the expert can see carry the hatched band. Nothing else on the
 * sign-in page needs to explain what this console is.
 */
function ThesisPanel() {
  const rows: { label: string; detail: string; hidden: boolean; kind: 'turn' | 'run' | 'stop' }[] = [
    { label: 'Client', detail: 'Add VAT to the pricing page', hidden: false, kind: 'turn' },
    { label: 'Run 12', detail: '4 files across 2 repos · 2:41', hidden: false, kind: 'run' },
    { label: 'Client', detail: 'Marked done — handed to review', hidden: false, kind: 'turn' },
    { label: 'You', detail: 'Move the VAT constant into config', hidden: true, kind: 'turn' },
    { label: 'Run 13', detail: 'edit · shell · read · 1:08', hidden: true, kind: 'run' },
    { label: 'Promotion', detail: 'staging · 2 repos merged, deployed', hidden: true, kind: 'stop' },
  ];

  return (
    <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-rule bg-surface px-12 py-14 lg:flex">
      <div>
        <Mark className="h-6 w-6 text-ink" />
        <p className="mt-10 max-w-md text-2xl font-semibold leading-[32px] tracking-[-0.02em] text-ink">
          One thread. Two audiences. Everything you write here is invisible to the client.
        </p>
      </div>

      <div className="relative mt-12 max-w-md">
        <span aria-hidden className="absolute bottom-2 left-[13px] top-2 w-px bg-rule" />
        <ul className="m-0 list-none p-0">
          {rows.map((row) => (
            <li key={`${row.label}-${row.detail}`} className={cn('relative py-1.5 pl-7', row.hidden && 'pl-9')}>
              {row.hidden ? (
                <span aria-hidden className="hatch-hidden absolute bottom-0 left-[28px] top-0 w-[3px] rounded-[1px]" />
              ) : null}

              <span aria-hidden className="absolute left-[8px] top-[9px]">
                {row.kind === 'turn' ? (
                  <span
                    className={cn(
                      'block h-[9px] w-[9px] rotate-45 border bg-surface',
                      row.hidden ? 'border-hidden' : 'border-rule-strong',
                    )}
                  />
                ) : row.kind === 'stop' ? (
                  <span className="block h-[11px] w-[11px] rounded-full bg-action" />
                ) : (
                  <span
                    className={cn(
                      'block h-[11px] w-[11px] rounded-full border-[1.4px] bg-surface',
                      row.hidden ? 'border-hidden' : 'border-ink-3',
                    )}
                  />
                )}
              </span>

              <div className="flex flex-wrap items-baseline gap-x-2">
                <span
                  className={cn(
                    'font-mono text-tiny uppercase tracking-[0.1em]',
                    row.hidden ? 'text-hidden' : 'text-ink-3',
                  )}
                >
                  {row.hidden ? (
                    <span className="inline-flex items-center gap-1">
                      <HiddenGlyph className="h-[9px] w-[9px]" />
                      {row.label}
                    </span>
                  ) : (
                    row.label
                  )}
                </span>
                <span className="text-sm text-ink-2">{row.detail}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-12 max-w-sm font-mono text-tiny leading-[17px] tracking-[0.05em] text-ink-3">
        The hatch marks a turn that is filtered out on every client read path and never appears on the client stream.
      </p>
    </aside>
  );
}

function GoogleGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 18 18" className="h-4 w-4 shrink-0 fill-current">
      <path d="M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.5h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.6z" opacity=".95" />
      <path d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3A9 9 0 0 0 9 18z" opacity=".75" />
      <path d="M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8l3-2.3z" opacity=".6" />
      <path d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 .9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6z" opacity=".85" />
    </svg>
  );
}
